import { create } from 'zustand';

import { PDFResult } from '../models/pdfModal';

type DotPrefix<T extends string, U extends string> = `${T}.${U}`;

type DotNestedKeys<T> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? DotPrefix<K, DotNestedKeys<T[K]>>
        : K;
    }[keyof T & string]
  : never;

export type RiskControlQuestionIdType = DotNestedKeys<RiskControlQuestionIds>;

export interface AssessingRiskManagementFrameworkIds {
  establishedRiskManagementFramework: string;
  boardPerceivesRiskManagementIntegral: string;
  ownershipDefinedAndAccepted: string;
  rmcChairedByIndependentDirector: string;
  rmcApprovedPolicies: string;
  rmcDefinedRiskAppetite: string;
  ceoChairedManagementCommittee: string;
  proceduresForManagingSignificantRisks: string;
  boardAwareOfHighRiskAreas: string;
  riskProfilesEstablished: string;
  legalAndRegulatoryObligationsIdentified: string;
  systemCharacteristicsForIdentifyingRisks: string;
  systemForFinancialStatementRisks: string;
  residualRiskReportedToBoard: string;
  majorRisksGiveRiseToActions: string;
  mechanismForChangingObjectives: string;
  earlyWarningIndicatorsInPlace: string;
  boardDiscussesRiskProfiles: string;
  riskAssessmentResultsShared: string;
  riskInformationCascadedToUnits: string;
  mandateScopeOfGovernanceFunctionsAligned: string;
}

export interface ControlEnvironmentAndActivitiesIds {
  cultureSupportsObjectives: string;
  seniorManagementCommitment: string;
  authoritiesClearlyDefined: string;
  decisionsAndActionsCoordinated: string;
  expectationsCommunicatedToEmployees: string;
  employeesHaveKnowledgeAndTools: string;
  businessContinuityProcessesInPlace: string;
  successionPlanningEffective: string;
}

export interface InformationAndCommunicationIds {
  boardReceivesTimelyReports: string;
  informationNeedsReassessed: string;
  reportingProceduresEffective: string;
  channelsForReportingBreaches: string;
  whistleblowingMechanismIndependent: string;
}

export interface MonitoringIds {
  ongoingProcessesEmbedded: string;
  riskOwnersProvideAssurance: string;
  processesReevaluateRisks: string;
  effectiveFollowUpProcedures: string;
  communicationToBoardOnMonitoringEffectiveness: string;
  arrangementsForManagementMonitoring: string;
  ceoOrCfoProvidesAssurance: string;
}

export interface RiskControlQuestionIds {
  assessingRiskManagementFramework: AssessingRiskManagementFrameworkIds;
  controlEnvironmentAndActivities: ControlEnvironmentAndActivitiesIds;
  informationAndCommunication: InformationAndCommunicationIds;
  monitoring: MonitoringIds;
}

interface ReportStore {
  principalReportPDF: PDFResult | null;
  setPrincipalReportPDF: (_: PDFResult | null) => void;

  riskControlReportPDF: PDFResult | null;
  setRiskControlReportPDF: (_: PDFResult | null) => void;

  riskManagementReportPDF: PDFResult | null;
  setRiskManagementReportPDF: (_: PDFResult | null) => void;

  summaryReportPDF: PDFResult | null;
  setSummaryReportPDF: (_: PDFResult | null) => void;

  controlReport: RiskControlQuestionIds;
  setControlReport: (_: RiskControlQuestionIds) => void;

  statementReportCompany: string;
  setStatementReportCompany: (_: string) => void;

  isGeneratingReport: boolean;
  setIsGeneratingReport: (_: boolean) => void;
}

const defaultRiskControlQuestionIds: RiskControlQuestionIds = {
  assessingRiskManagementFramework: {
    establishedRiskManagementFramework: '',
    boardPerceivesRiskManagementIntegral: '',
    ownershipDefinedAndAccepted: '',
    rmcChairedByIndependentDirector: '',
    rmcApprovedPolicies: '',
    rmcDefinedRiskAppetite: '',
    ceoChairedManagementCommittee: '',
    proceduresForManagingSignificantRisks: '',
    boardAwareOfHighRiskAreas: '',
    riskProfilesEstablished: '',
    legalAndRegulatoryObligationsIdentified: '',
    systemCharacteristicsForIdentifyingRisks: '',
    systemForFinancialStatementRisks: '',
    residualRiskReportedToBoard: '',
    majorRisksGiveRiseToActions: '',
    mechanismForChangingObjectives: '',
    earlyWarningIndicatorsInPlace: '',
    boardDiscussesRiskProfiles: '',
    riskAssessmentResultsShared: '',
    riskInformationCascadedToUnits: '',
    mandateScopeOfGovernanceFunctionsAligned: '',
  },
  controlEnvironmentAndActivities: {
    cultureSupportsObjectives: '',
    seniorManagementCommitment: '',
    authoritiesClearlyDefined: '',
    decisionsAndActionsCoordinated: '',
    expectationsCommunicatedToEmployees: '',
    employeesHaveKnowledgeAndTools: '',
    businessContinuityProcessesInPlace: '',
    successionPlanningEffective: '',
  },
  informationAndCommunication: {
    boardReceivesTimelyReports: '',
    informationNeedsReassessed: '',
    reportingProceduresEffective: '',
    channelsForReportingBreaches: '',
    whistleblowingMechanismIndependent: '',
  },
  monitoring: {
    ongoingProcessesEmbedded: '',
    riskOwnersProvideAssurance: '',
    processesReevaluateRisks: '',
    effectiveFollowUpProcedures: '',
    communicationToBoardOnMonitoringEffectiveness: '',
    arrangementsForManagementMonitoring: '',
    ceoOrCfoProvidesAssurance: '',
  },
};

export const useReportStore = create<ReportStore>((set) => ({
  principalReportPDF: null,
  setPrincipalReportPDF: (data: PDFResult | null) =>
    set({ principalReportPDF: data }),

  riskControlReportPDF: null,
  setRiskControlReportPDF: (data: PDFResult | null) =>
    set({ riskControlReportPDF: data }),

  riskManagementReportPDF: null,
  setRiskManagementReportPDF: (data: PDFResult | null) =>
    set({ riskManagementReportPDF: data }),

  summaryReportPDF: null,
  setSummaryReportPDF: (data: PDFResult | null) =>
    set({ summaryReportPDF: data }),

  statementReportCompany: '',
  setStatementReportCompany: (data: string) =>
    set({ statementReportCompany: data }),

  controlReport: defaultRiskControlQuestionIds,
  setControlReport: (data: RiskControlQuestionIds) =>
    set({ controlReport: data }),

  isGeneratingReport: false,
  setIsGeneratingReport: (data: boolean) => set({ isGeneratingReport: data }),
}));
